import { motion } from 'motion/react';

type AboutSanctuaryProps = {
	eyebrow?: string;
	title?: string;
	description?: string;
	statOneValue?: string;
	statOneLabel?: string;
	statTwoValue?: string;
	statTwoLabel?: string;
	statThreeValue?: string;
	statThreeLabel?: string;
};

export function AboutSanctuary({
	eyebrow,
	title,
	description,
	statOneValue,
	statOneLabel,
	statTwoValue,
	statTwoLabel,
	statThreeValue,
	statThreeLabel,
}: AboutSanctuaryProps) {
	const stats = [
		{ value: statOneValue   || '100+', label: statOneLabel   || 'Years of Sikh remembrance and seva' },
		{ value: statTwoValue   || '7',    label: statTwoLabel   || 'Days a week open for prayer and sangat' },
		{ value: statThreeValue || '1',    label: statThreeLabel || 'Shared home for worship, learning, and langar' },
	];

	return (
		<section className="relative overflow-hidden border-y border-border bg-card">
			{/* Atmospheric centre-top glow */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[0.055] blur-[150px]"
			/>

			<div className="relative mx-auto max-w-6xl px-6 py-24 md:px-12 lg:px-16">

				{/* ── Stats row ──────────────────────────────────── */}
				<div className="grid grid-cols-1 sm:grid-cols-3">
					{stats.map((stat, i) => (
						<motion.div
							key={stat.label}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.06 }}
							transition={{ duration: 0.6, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }}
							className={[
								'group relative flex flex-col py-10',
								'border-border/60',
								// vertical dividers on desktop
								i > 0 ? 'sm:border-l sm:pl-10' : '',
								i < stats.length - 1 ? 'sm:pr-10' : '',
								// horizontal dividers on mobile
								i > 0 ? 'border-t sm:border-t-0' : '',
							].join(' ')}
						>
							{/* Gold accent line above value */}
							<div
								aria-hidden="true"
								className="mb-5 h-px w-8 bg-accent/50 transition-all duration-500 group-hover:w-14 group-hover:bg-accent/80"
							/>

							{/* Value */}
							<p className="font-serif text-[3.5rem] font-medium leading-none tracking-[-0.03em] text-foreground md:text-[4.25rem]">
								{stat.value}
							</p>

							{/* Label */}
							<p className="mt-4 max-w-[18ch] text-[13px] leading-[1.85] text-foreground/42">
								{stat.label}
							</p>
						</motion.div>
					))}
				</div>

				{/* ── Text block ─────────────────────────────────── */}
				<motion.div
					initial={{ opacity: 0, y: 22 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.06 }}
					transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
					className="mt-4 grid gap-12 border-t border-border/60 pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-24"
				>
					{/* Left */}
					<div>
						<div className="flex items-center gap-3">
							<div className="h-px w-6 shrink-0 bg-accent/60" aria-hidden="true" />
							<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/75">
								{eyebrow || 'A place of prayer and belonging'}
							</p>
						</div>
						<h2 className="mt-6 font-serif text-4xl leading-[1.02] tracking-tight text-foreground md:text-5xl">
							{title || 'A quiet, welcoming home built around sangat, seva, and clarity.'}
						</h2>
					</div>

					{/* Right */}
					<div className="flex flex-col justify-end gap-6">
						<div className="flex items-start gap-4">
							<div className="mt-2 h-px w-8 shrink-0 bg-foreground/15" aria-hidden="true" />
							<p className="text-[14.5px] leading-[1.9] text-foreground/42">
								{description || 'Gurdwara Shahidan Char Sahibzade serves as a calm spiritual and community space — open, respectful, and built to serve the sangat.'}
							</p>
						</div>

						{/* Decorative horizontal rule with gold centre dot */}
						<div className="flex items-center gap-3 pt-2">
							<div className="h-px flex-1 bg-gradient-to-r from-border/70 to-transparent" aria-hidden="true" />
							<div className="h-1 w-1 rounded-full bg-accent/60 shadow-[0_0_5px_rgba(184,154,50,0.55)]" aria-hidden="true" />
							<div className="h-px w-8 bg-border/40" aria-hidden="true" />
						</div>
					</div>
				</motion.div>
			</div>
		</section>
	);
}
